import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from '@saleshandy/icons';
import { useOutsideClick } from 'rooks';
import moment from 'moment-timezone';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { FilterOperation, ProspectFilterField } from '../../../types';

import ProspectFiltersSelectDropdown from '../../prospect-filters-select-dropdown';

import {
  getIsCustomDateSelected,
  getProspectFiltersDateDropdownOptions,
  operationsWithStartAndEndDate,
} from '../../../utils/get-prospect-filters-date-dropdown-options';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
} from '../../../../../../../shared/design-system/components/overlay';
import { accessibleOnClick } from '../../../../../../../shared/utils/accessible-on-click';
import { getUserTimeZone } from '../../../../../../../shared/utils/user-details';
import { formatDateTooltipTxt } from '../../../utils/helpers';

type IProps = {
  field: ProspectFilterField;
  operation: FilterOperation;
  value: string;
  onUpdateFilterConditionValue: (value: string | string[]) => void;
  disabled?: boolean;
};

const ProspectFiltersDateInput: React.FC<IProps> = ({
  field,
  operation,
  value,
  onUpdateFilterConditionValue,
  disabled = false,
}) => {
  const [showCustomDateInput, setShowCustomDateInput] = useState(false);

  const getCurrentDate = (): string => moment().toISOString();

  const getFormattedDate = (date: string): string =>
    moment(date).format('MMM DD, YYYY');

  const getMonth = (date: Date): string => moment(date).format('MMMM YYYY');

  const options = getProspectFiltersDateDropdownOptions(operation);

  const ref = useRef<HTMLDivElement>(null);

  const handleClickOutside = () => {
    if (showCustomDateInput) {
      setShowCustomDateInput(false);
    }
  };

  useOutsideClick(ref, handleClickOutside);

  const canSelectDateRange = operationsWithStartAndEndDate.includes(operation);

  const getPlaceholderText = () => {
    if (field?.key === 'lastUpdatedAt') {
      return 'Select Last Updated Date';
    }
    return `Select ${field?.value || 'Value'}`;
  };

  return (
    <>
      <OverlayPopover
        show={showCustomDateInput}
        content={
          <div
            {...accessibleOnClick(() => {
              setShowCustomDateInput(true);
            })}
          >
            <DatePicker
              dateFormat="MMM d, yyyy"
              selected={
                Array.isArray(value) && value[0] ? new Date(value[0]) : null
              }
              startDate={
                Array.isArray(value) && value[0] ? new Date(value[0]) : null
              }
              endDate={
                Array.isArray(value) && value[1] ? new Date(value[1]) : null
              }
              onChange={(dates) => {
                if (canSelectDateRange) {
                  const [start, end] = dates;

                  onUpdateFilterConditionValue([start, end]);

                  if (end) {
                    setTimeout(() => {
                      setShowCustomDateInput(false);
                    }, 100);
                  }
                } else {
                  onUpdateFilterConditionValue([dates]);
                  setTimeout(() => {
                    setShowCustomDateInput(false);
                  }, 100);
                }
              }}
              inline
              showDisabledMonthNavigation
              selectsRange={canSelectDateRange}
              calendarStartDay={1}
              renderCustomHeader={({
                date,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }) => (
                <div className="pick-date-and-time-header">
                  <p>{getMonth(date)}</p>
                  <div className="pick-date-and-time-action-btns">
                    <button
                      type="button"
                      onClick={decreaseMonth}
                      disabled={prevMonthButtonDisabled}
                    >
                      <ChevronLeft />
                    </button>
                    <button
                      type="button"
                      onClick={increaseMonth}
                      disabled={nextMonthButtonDisabled}
                    >
                      <ChevronRight />
                    </button>
                  </div>
                </div>
              )}
            />
          </div>
        }
        placement={Placement.BottomStart}
        className="prospect-filters-date-picker-popover"
      >
        <ProspectFiltersSelectDropdown
          className="date-input-select-box w-100"
          selectClassName="date-input-dropdown"
          options={options}
          placeholder={getPlaceholderText()}
          disabled={disabled}
          selectedOptionKey={(() => {
            if (!value) {
              return null;
            }

            if (
              Array.isArray(value) &&
              value?.length !== 0 &&
              getIsCustomDateSelected(options, value?.[0])
            ) {
              return 'custom';
            }

            return value;
          })()}
          optionRenderer={(option) => {
            const timeZone = getUserTimeZone();
            const date = formatDateTooltipTxt(
              option?.startDate,
              option?.endDate,
              timeZone,
            );

            return (
              <OverlayTooltip text={date} className="tooltip-lg">
                <span>{option.value}</span>
              </OverlayTooltip>
            );
          }}
          selectedOptionRenderer={(option) => {
            if (option.key === 'custom') {
              if (value?.length === 2) {
                let startDate = '';
                let endDate = '';

                if (value[0]) {
                  startDate = getFormattedDate(value[0]);
                }
                if (value[1]) {
                  endDate = getFormattedDate(value[1]);
                }

                return `${startDate} ${endDate ? `- ${endDate}` : ''}`.trim();
              }

              return getFormattedDate(value?.[0]);
            }

            return <span>{option.value}</span>;
          }}
          onChange={(option) => {
            if (option.key === 'custom') {
              setShowCustomDateInput(true);

              const cDate = getCurrentDate();

              if (canSelectDateRange) {
                if (value?.length === 2) {
                  onUpdateFilterConditionValue([value[0], value[1]]);
                } else {
                  onUpdateFilterConditionValue([cDate, cDate]);
                }
              } else {
                onUpdateFilterConditionValue([cDate]);
              }
            } else {
              onUpdateFilterConditionValue(option.key);
            }
          }}
          parentRef={ref}
        />
      </OverlayPopover>
    </>
  );
};

export default ProspectFiltersDateInput;
